/**
 * Shared pieces of the Usage area (prototype docs/prototype.html 758 `spark`, 4111 `uBars`, 4121 `uLine`).
 * Every number drawn here comes from GET /usage/clients/{id} — the charts only lay it out.
 */
import type { ClientProductUsage } from '@amplispot/contracts';
import { FORECAST_UNEVEN, forecastTooUneven, monthShort, nf, usableForecast } from '../../lib/usage';

/**
 * How much of a 14-day series is real. Until daily snapshots build up the engine fills the earlier days with a
 * straight line from the client's usual level to today (`is_estimated`), so a line drawn from those alone says
 * nothing about the trend. Under 2 real points there is no line worth drawing (ADR-001 usage_daily: label estimates).
 */
export function daysInfo(days: ClientProductUsage['days']) {
  const real = days.filter((d) => d.util != null && !d.is_estimated).length;
  const est = days.some((d) => d.util != null && d.is_estimated);
  return { real, est, drawable: real >= 2 };
}

/** The label under a 14-day line, saying when part of it is estimated. */
export const daysLabel = (days: ClientProductUsage['days']) => (daysInfo(days).est ? 'Last 14 days, partly estimated' : 'Last 14 days');

/**
 * Polyline runs over (x, y, estimated) points: a segment is estimated when either end is. Gaps (null) break the line.
 * Real runs are drawn solid; estimated runs dashed and muted, so a guessed stretch never reads as measured.
 */
export function runs(pts: ({ x: number; y: number; e: boolean } | null)[]) {
  const out: { e: boolean; pts: { x: number; y: number }[] }[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    if (!a || !b) continue;
    const e = a.e || b.e;
    const cur = out[out.length - 1];
    if (cur && cur.e === e && cur.pts[cur.pts.length - 1] === a) cur.pts.push(b);
    else out.push({ e, pts: [a, b] });
  }
  return out;
}
const ptsAttr = (pts: { x: number; y: number }[]) => pts.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' ');

/**
 * Prototype `spark()`: a small line with a dot on the last point. `est[i]` marks values[i] as estimated (drawn
 * dashed and muted). With `est`, fewer than 2 real points shows "no daily data yet" instead of an invented line.
 */
export function Spark({ values, est, w, h, color = '#5B4BE8' }: { values: (number | null)[] | null; est?: boolean[]; w: number; h: number; color?: string }) {
  const all = (values ?? []).map((v, i) => (v == null ? null : { v, e: !!est?.[i] })).filter((q): q is { v: number; e: boolean } => q != null);
  const realN = all.filter((q) => !q.e).length;
  if (all.length < 2 || (est && realN < 2)) return <span className="sub" style={{ color: 'var(--mute)', whiteSpace: 'nowrap' }}>{all.length && est ? 'no daily data yet' : 'no data'}</span>;
  const mx = Math.max(...all.map((q) => q.v));
  const mn = Math.min(...all.map((q) => q.v));
  const r = mx - mn || 1;
  const pts = all.map((q, i) => ({ x: (i / (all.length - 1)) * (w - 4) + 2, y: h - 3 - ((q.v - mn) / r) * (h - 6), e: q.e }));
  const last = pts[pts.length - 1]!;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {runs(pts).map((s, i) => (
        <polyline
          key={i}
          points={ptsAttr(s.pts)}
          fill="none"
          stroke={s.e ? 'var(--mute)' : color}
          strokeWidth={s.e ? 1.5 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          {...(s.e ? { strokeDasharray: '3 3' } : {})}
        />
      ))}
      <circle cx={last.x} cy={last.y} r="3" fill={last.e ? 'var(--mute)' : color} stroke="#fff" strokeWidth="2" />
    </svg>
  );
}

/** Prototype `uBars`: six past months, this period, and the 30-day forecast, against the plan line. */
export function MonthBars({ p, asOf }: { p: ClientProductUsage; asOf: string }) {
  const W = 620, H = 210, top = 24, bot = 30, left = 8, right = 8, n = 8, gap = 14;
  const bw = (W - left - right - gap * (n - 1)) / n;
  const ch = H - top - bot;
  const plan = p.plan ?? 0;
  const months = p.months.slice(-6);
  const fcv = usableForecast(p.forecast);
  const vals: (number | null)[] = [...months.map((m) => m.value), p.used, fcv ? fcv.forecast_abs : null];
  const lbl = [...months.map((m) => monthShort(m.period)), monthShort(asOf.slice(0, 7)), 'Forecast'];
  const mxv = Math.max(plan * 1.12, ...vals.filter((v): v is number => v != null), 1) * 1.04;
  const y = (v: number) => top + ch - (v / mxv) * ch;
  const fc = months.length + 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${p.main_metric} by month for the last six months, this period and the forecast`} style={{ display: 'block', maxWidth: 760 }}>
      {plan ? (
        <>
          <line x1={left} x2={W - right} y1={y(plan)} y2={y(plan)} stroke="var(--mute)" strokeWidth="1.5" strokeDasharray="6 5" />
          <text x={W - right} y={y(plan) - 6} textAnchor="end" fontSize="11.5" fill="var(--sub)">
            Plan {nf(plan)}
          </text>
        </>
      ) : null}
      {forecastTooUneven(p.forecast) ? (
        <g>
          <text x={left + fc * (bw + gap) + bw / 2} y={top + ch - 22} textAnchor="middle" fontSize="11" fill="var(--sub)">
            <tspan x={left + fc * (bw + gap) + bw / 2}>Too uneven</tspan>
            <tspan x={left + fc * (bw + gap) + bw / 2} dy="14">to forecast</tspan>
          </text>
          <text x={left + fc * (bw + gap) + bw / 2} y={H - 9} textAnchor="middle" fontSize="12" fill="var(--sub)">
            Forecast
          </text>
        </g>
      ) : null}
      {vals.map((v, i) => {
        if (v == null) return null;
        const x = left + i * (bw + gap);
        const yy = y(v);
        const r = Math.min(6, (top + ch - yy) / 2);
        const isCur = i === months.length;
        const isFc = i === fc;
        const d = `M${x},${top + ch} V${yy + r} Q${x},${yy} ${x + r},${yy} H${x + bw - r} Q${x + bw},${yy} ${x + bw},${yy + r} V${top + ch} Z`;
        const est = i < months.length && months[i]!.is_estimated;
        const tip = `${lbl[i]}${isFc ? ' (forecast)' : est ? ' (estimated)' : ''}: ${nf(v)} ${p.unit}${plan ? `, ${Math.round((v / plan) * 100)}% of plan` : ''}`;
        return (
          <g key={i}>
            <title>{tip}</title>
            <path d={d} fill={isFc ? 'none' : isCur ? 'var(--brand)' : '#CFC9FA'} {...(isFc ? { stroke: 'var(--brand)', strokeWidth: 2, strokeDasharray: '5 4' } : {})} />
            <text x={x + bw / 2} y={yy - 7} textAnchor="middle" fontSize="12" fontWeight={isCur ? 700 : 600} fill="var(--ink)">
              {nf(v)}
            </text>
            <text x={x + bw / 2} y={H - 9} textAnchor="middle" fontSize="12" fill="var(--sub)">
              {lbl[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Prototype `uLine`: the last 14 days as % of plan, the 30-day forecast, and the low-use line. */
export function DaysLine({ p }: { p: ClientProductUsage }) {
  const s = p.days.map((d) => d.util);
  const W = 620, H = 150, top = 14, bot = 26, left = 8, right = 8, total = 44;
  const cw = W - left - right;
  const ch = H - top - bot;
  const f = usableForecast(p.forecast);
  const known = s.filter((v): v is number => v != null);
  const mx = Math.max(100, ...known, f ? f.util_in_30d : 0) * 1.05;
  const last = s.length - 1;
  const x = (i: number) => left + (i / (total - 1)) * cw;
  const y = (v: number) => top + ch - (v / mx) * ch;
  // Gaps (no data that day) break the line rather than drawing through zero; estimated days are dashed and muted.
  const segs = runs(s.map((v, i) => (v == null ? null : { x: x(i), y: y(v), e: p.days[i]!.is_estimated })));
  const today = s[last] ?? p.util;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Last 14 days as a percent of plan${daysInfo(p.days).est ? ', partly estimated' : ''}, with the 30-day forecast`} style={{ display: 'block', maxWidth: 760 }}>
      <line x1={left} x2={W - right} y1={y(p.low_line)} y2={y(p.low_line)} stroke="#E11D48" strokeOpacity=".55" strokeWidth="1.5" strokeDasharray="6 5" />
      <text x={W - right} y={y(p.low_line) - 5} textAnchor="end" fontSize="11.5" fill="var(--sub)">
        Low-use line {p.low_line}%
      </text>
      <line x1={x(last)} x2={x(last)} y1={top} y2={top + ch} stroke="var(--line)" />
      {segs.map((g, i) => (
        <polyline
          key={i}
          points={ptsAttr(g.pts)}
          fill="none"
          stroke={g.e ? 'var(--mute)' : 'var(--brand)'}
          strokeWidth={g.e ? 2 : 2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          {...(g.e ? { strokeDasharray: '4 4' } : {})}
        >
          {g.e ? <title>Estimated: no daily figure was recorded for these days</title> : null}
        </polyline>
      ))}
      {f && today != null ? (
        <>
          <path d={`M${x(last).toFixed(1)},${y(today).toFixed(1)} L${x(total - 1).toFixed(1)},${y(f.util_in_30d).toFixed(1)}`} fill="none" stroke="var(--brand)" strokeWidth="2.5" strokeDasharray="3 6" strokeLinecap="round" />
          <circle cx={x(total - 1)} cy={y(f.util_in_30d)} r="4.5" fill="var(--surface)" stroke="var(--brand)" strokeWidth="2" />
          <text x={x(total - 1) - 8} y={y(f.util_in_30d) - 9} textAnchor="end" fontSize="12" fontWeight="700" fill="var(--ink)">
            {Math.round(f.util_in_30d)}% of plan
          </text>
        </>
      ) : null}
      {forecastTooUneven(p.forecast) ? (
        <text x={W - right} y={top + ch / 2} textAnchor="end" fontSize="12" fill="var(--sub)">
          {FORECAST_UNEVEN}
        </text>
      ) : null}
      {today != null ? (
        <>
          <circle cx={x(last)} cy={y(today)} r="4.5" fill="var(--brand)" />
          <text x={x(last)} y={y(today) - 9} textAnchor="middle" fontSize="12" fontWeight="700" fill="var(--ink)">
            {Math.round(today)}%
          </text>
        </>
      ) : null}
      <text x={left} y={H - 7} fontSize="12" fill="var(--sub)">
        14 days ago
      </text>
      <text x={x(last)} y={H - 7} textAnchor="middle" fontSize="12" fill="var(--sub)">
        Today
      </text>
      <text x={W - right} y={H - 7} textAnchor="end" fontSize="12" fill="var(--sub)">
        In 30 days
      </text>
    </svg>
  );
}
